/**
 * Auth token store. Holds the JWT issued by the dev IdP (or Cognito in prod),
 * persisted to localStorage so reloads restore the session. Expiry is tracked
 * so we clear stale tokens before they 401.
 *
 * The stored user carries the cost/margin visibility flags so the UI can hide
 * affordances the API would redact anyway (defense in depth — the API is the
 * source of truth; never trust the client to enforce visibility).
 */

import { create } from 'zustand';
import type { AppUserRole } from '@kdental/shared';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: AppUserRole;
  spireSalespersonCode: string | null;
  canViewCost: boolean;
  canViewMargin: boolean;
  /** Optional for sessions stored before this flag existed — treat absent as true. */
  canOverridePrice?: boolean;
  /** Optional for sessions stored before this flag existed — treat absent as true. */
  canPlaceOrders?: boolean;
  /** Annual sales goal in dollars; 0/absent = no goal (no progress bar). */
  salesTargetAnnual?: number;
}

interface AuthState {
  token: string | null;
  expiresAt: number | null;
  /** Cognito refresh token — survives ID-token expiry so sessions renew silently. */
  refreshToken: string | null;
  user: AuthUser | null;

  setSession(input: { token: string; expiresIn: number; user: AuthUser; refreshToken?: string | null }): void;
  /** Set/replace the token pair without touching the stored user (Cognito
   * callback sets the token first, then fetches /users/me; silent refresh
   * replaces only the token). */
  setToken(input: { token: string; expiresIn: number; refreshToken?: string | null }): void;
  setUser(user: AuthUser): void;
  clear(): void;
  isAuthenticated(): boolean;
}

const TOKEN_KEY = 'kdental:auth:token';
const EXPIRES_KEY = 'kdental:auth:expiresAt';
const REFRESH_KEY = 'kdental:auth:refreshToken';
const USER_KEY = 'kdental:auth:user';

interface Persisted {
  token: string | null;
  expiresAt: number | null;
  refreshToken: string | null;
  user: AuthUser | null;
}

function load(): Persisted {
  if (typeof window === 'undefined')
    return { token: null, expiresAt: null, refreshToken: null, user: null };
  try {
    const token = window.localStorage.getItem(TOKEN_KEY);
    const expiresAtRaw = window.localStorage.getItem(EXPIRES_KEY);
    const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : null;
    const refreshToken = window.localStorage.getItem(REFRESH_KEY);
    const userRaw = window.localStorage.getItem(USER_KEY);
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
    if (token && expiresAt && expiresAt < Date.now()) {
      // Stale access token. Keep the refresh token + user so the router guard
      // can attempt a silent Cognito refresh instead of forcing re-login.
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(EXPIRES_KEY);
      return { token: null, expiresAt: null, refreshToken, user };
    }
    return { token, expiresAt, refreshToken, user };
  } catch {
    return { token: null, expiresAt: null, refreshToken: null, user: null };
  }
}

function persist(state: Persisted): void {
  if (typeof window === 'undefined') return;
  try {
    if (state.token) window.localStorage.setItem(TOKEN_KEY, state.token);
    else window.localStorage.removeItem(TOKEN_KEY);
    if (state.expiresAt !== null) window.localStorage.setItem(EXPIRES_KEY, String(state.expiresAt));
    else window.localStorage.removeItem(EXPIRES_KEY);
    if (state.refreshToken) window.localStorage.setItem(REFRESH_KEY, state.refreshToken);
    else window.localStorage.removeItem(REFRESH_KEY);
    if (state.user) window.localStorage.setItem(USER_KEY, JSON.stringify(state.user));
    else window.localStorage.removeItem(USER_KEY);
  } catch {
    /* private mode — ignore */
  }
}

const initial = load();

export const useAuth = create<AuthState>((set, get) => ({
  token: initial.token,
  expiresAt: initial.expiresAt,
  refreshToken: initial.refreshToken,
  user: initial.user,

  setSession({ token, expiresIn, user, refreshToken }) {
    const expiresAt = Date.now() + expiresIn * 1000;
    const next = { token, expiresAt, refreshToken: refreshToken ?? null, user };
    set(next);
    persist(next);
  },
  setToken({ token, expiresIn, refreshToken }) {
    const expiresAt = Date.now() + expiresIn * 1000;
    const next = {
      token,
      expiresAt,
      refreshToken: refreshToken !== undefined ? refreshToken : get().refreshToken,
      user: get().user,
    };
    set(next);
    persist(next);
  },
  setUser(user) {
    set({ user });
    persist({ ...get(), user });
  },
  clear() {
    const next = { token: null, expiresAt: null, refreshToken: null, user: null };
    set(next);
    persist(next);
  },
  isAuthenticated() {
    const { token, expiresAt } = get();
    if (!token) return false;
    if (expiresAt && expiresAt < Date.now()) return false;
    return true;
  },
}));

/** Module-level synchronous accessor for the request layer (api.ts isn't a hook). */
export function currentAccessToken(): string | null {
  const { token, expiresAt } = useAuth.getState();
  if (!token) return null;
  if (expiresAt && expiresAt < Date.now()) return null;
  return token;
}
